import { ColumnState } from "ag-grid-community";
import { create } from "zustand";
import { formatDateMonth, formatDateYear4 } from "src/utils/string/stringUtil";
import { PRD1020_R01Dto } from "./Dto_PRD1020_R01";

/** 화면 검색조건. itemNm은 선택한 품목코드 옆 표시용이며 조회 API에는 전달하지 않는다. */
export interface PRD1020_R01Filter {
	compCd: string;
	yearMonth: string;
	brandCd: string;
	itemGroup: string;
	itemAccount: string;
	itemCd: string;
	itemNm: string;
}

/**
 * PRD1020_R01 화면 전용 Zustand Store 계약이다.
 * 현재/직전 검색조건, 조회 결과와 공통 Grid의 컬럼·스크롤 상태만 보관하며
 * API 호출은 Store가 아닌 Content_PRD1020_R01에서 수행한다.
 */
export interface Store_PRD1020_R01 {
	/** 사용자가 현재 편집 중인 검색조건 */
	PRD1020_R01Filter: PRD1020_R01Filter;
	/** 마지막으로 조회에 사용한 검색조건의 복사본 */
	PRD1020_R01PastFilter: PRD1020_R01Filter;
	setPRD1020_R01Filter: (filter: Partial<PRD1020_R01Filter>) => void;
	setPRD1020_R01PastFilter: (filter: PRD1020_R01Filter) => void;

	/** 백엔드 병합 결과에 Grid 식별 인덱스를 부여한 표시 목록 */
	PRD1020_R01List: PRD1020_R01Dto[];
	setPRD1020_R01List: (list: PRD1020_R01Dto[]) => void;

	/** 컬럼 순서·너비·숨김 상태. null은 사용자 저장 상태를 아직 적용하지 않은 상태다. */
	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;
	/** Grid 세로 스크롤 복원 위치. 새 조회와 초기화 시 null로 되돌린다. */
	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;

	initData: () => void;
}

/** 호출 시점의 현재 월을 반영하기 위해 상수가 아닌 함수로 기본 검색조건을 생성한다. */
const createDefaultFilter = (): PRD1020_R01Filter => ({
	compCd: "",
	yearMonth: `${formatDateYear4(new Date())}-${formatDateMonth(new Date())}`,
	brandCd: "",
	itemGroup: "",
	itemAccount: "",
	itemCd: "",
	itemNm: "",
});

export const defaultPRD1020_R01Filter = createDefaultFilter();

export const usePRD1020_R01Store = create<Store_PRD1020_R01>((set) => ({
	PRD1020_R01Filter: createDefaultFilter(),
	PRD1020_R01PastFilter: createDefaultFilter(),
	setPRD1020_R01Filter: (filter) => set((state) => ({
		PRD1020_R01Filter: { ...state.PRD1020_R01Filter, ...filter },
	})),
	setPRD1020_R01PastFilter: (filter) => set({ PRD1020_R01PastFilter: { ...filter } }),

	PRD1020_R01List: [],
	setPRD1020_R01List: (list) => set({ PRD1020_R01List: list }),

	columnState: null,
	setColumnState: (columnState) => set({ columnState }),
	scrollTop: null,
	setScrollTop: (scrollTop) => set({ scrollTop }),

	/*
	 * Actionbar 초기화와 메뉴 공통 초기화에서 사용한다.
	 * 검색조건만이 아니라 결과와 Grid 상태까지 함께 제거해 이전 조회 상태를 남기지 않는다.
	 */
	initData: () => set({
		PRD1020_R01Filter: createDefaultFilter(),
		PRD1020_R01PastFilter: createDefaultFilter(),
		PRD1020_R01List: [],
		columnState: null,
		scrollTop: null,
	}),
}));
